import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { collapse, CollapseState, trackByProp } from '@recruitee/common';

import { IconName } from '../../icons-container/icons.type';
import { AnimState, MenuItem } from '../interfaces';
import { ItemType } from '../sidebar-item/sidebar-item.component';
import { SidebarInternalService } from '../sidebar/sidebar-internal.service';

@Component({
  selector: 'rt-sidebar-group',
  templateUrl: './sidebar-group.component.html',
  styleUrls: ['./sidebar-group.component.less'],
  animations: [collapse],
})
export class SidebarGroupComponent implements OnInit {
  @Input() public heading: string;
  @Input() public icon: IconName;
  @Input() public items: MenuItem[];
  @Input() public initialExpanded: boolean = true;
  @Input() public ItemType: typeof ItemType = ItemType;
  @Input() public isFirst: boolean = false;
  @Input() public isLast: boolean = false;

  @Output() public state: EventEmitter<AnimState> = new EventEmitter();

  public readonly CollapseState: typeof CollapseState = CollapseState;
  public sidebarMinimized: boolean = false;
  public isMinimized: boolean;
  public trackByName = trackByProp<MenuItem>({ prop: 'name' });
  public get visibilityState(): CollapseState {
    return this.isMinimized || this.sidebarMinimized
      ? CollapseState.Collapsed
      : CollapseState.Normal;
  }

  private ngOnDestroy$ = new Subject<void>();
  private pageId: string | number | null = null;

  constructor(
    private cdRef: ChangeDetectorRef,
    private sidebarInternalService: SidebarInternalService,
    public router: Router,
    public route: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    this.isMinimized = !this.initialExpanded;

    this.sidebarInternalService.sidebarMinimized$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(this.onSidebarMinimize.bind(this));

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.route.snapshot.firstChild?.data?.pageId ?? null),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(pageId => {
        this.pageId = pageId;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onHeaderClick(): void {
    if (this.sidebarMinimized) {
      return;
    }

    this.toggleMinimized();
    this.state.emit({
      animState: this.isMinimized ? CollapseState.Collapsed : CollapseState.Normal,
    });
    if (!this.isMinimized) {
      this.router.navigateByUrl(this.items[0].url);
    }
    this.cdRef.detectChanges();
  }

  public toggleMinimized(value?: boolean): void {
    this.isMinimized = value != null ? value : !this.isMinimized;
  }

  public isActive(item: MenuItem) {
    if (this.pageId !== null) return item.id === this.pageId;
    return this.router.url.includes(item.url) || this.router.url.includes(item.upgradeUrl!);
  }
  private onSidebarMinimize(isMinimized: boolean): void {
    this.sidebarMinimized = isMinimized;
    this.cdRef.detectChanges();
  }
}
